import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  Provider,
  ViewEncapsulation,
  inject
} from '@angular/core';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_NATIVE_DATE_FORMATS,
  MatDateFormats,
  NativeDateAdapter
} from '@angular/material/core';
import { MatCalendar, MatDatepicker, MatDatepickerContent } from '@angular/material/datepicker';
import { Subscription } from 'rxjs';

/**
 * Shared "modern" date picker for the Health module.
 *
 *  - VG_DATE_PROVIDERS       -> every date input shows "Oct 13, 2026" (value stays a JS Date, so the existing
 *                               DatePipe 'MM-dd-yyyy' API payloads are untouched).
 *  - VgCalendarHeaderComponent -> month / year header with prev / next, plus the Clear / Today footer.
 *  - VgModernDatepickerDirective -> attaches the header to every <mat-datepicker> that sees this directive,
 *                               so no template needs a [calendarHeaderComponent] binding.
 */

/** Display format "MMM d, yyyy" (Oct 13, 2026). Parsing stays the adapter default. */
export const VG_DATE_FORMATS: MatDateFormats = {
  ...MAT_NATIVE_DATE_FORMATS,
  display: {
    ...MAT_NATIVE_DATE_FORMATS.display,
    dateInput: { year: 'numeric', month: 'short', day: 'numeric' },
    monthYearLabel: { year: 'numeric', month: 'long' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' }
  }
};

/** Native adapter that prints weekday headers as Sun Mon Tue ... instead of S M T ... */
export class VgDateAdapter extends NativeDateAdapter {
  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    return super.getDayOfWeekNames(style === 'narrow' ? 'short' : style);
  }
}

/** Put these in the `providers` of any component that has a date input. */
export const VG_DATE_PROVIDERS: Provider[] = [
  { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
  { provide: DateAdapter, useClass: VgDateAdapter },
  { provide: MAT_DATE_FORMATS, useValue: VG_DATE_FORMATS }
];

const YEARS_PER_PAGE = 24;

@Component({
  selector: 'vg-calendar-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./vg-datepicker.css'],
  template: `
    <div class="vg-dp-head">
      <button type="button" class="vg-dp-nav" (click)="step(-1)" [disabled]="!canStep(-1)" [attr.aria-label]="prevLabel">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
      </button>
      <button type="button" class="vg-dp-period" (click)="toggleView()" aria-live="polite"
              [attr.aria-label]="'Choose month and year, ' + periodLabel">{{ periodLabel }}</button>
      <button type="button" class="vg-dp-nav" (click)="step(1)" [disabled]="!canStep(1)" [attr.aria-label]="nextLabel">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
      </button>
    </div>
    <div class="vg-dp-foot">
      <button type="button" class="vg-dp-link" (click)="clear()">Clear</button>
      <button type="button" class="vg-dp-link" (click)="today()" [disabled]="!todayAllowed">Today</button>
    </div>
  `
})
export class VgCalendarHeaderComponent<D> implements OnDestroy {
  private readonly stateSub: Subscription;

  constructor(
    private readonly calendar: MatCalendar<D>,
    private readonly adapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private readonly formats: MatDateFormats,
    @Optional() private readonly content: MatDatepickerContent<any, D> | null,
    cdr: ChangeDetectorRef
  ) {
    this.stateSub = calendar.stateChanges.subscribe(() => cdr.markForCheck());
  }

  ngOnDestroy(): void {
    this.stateSub.unsubscribe();
  }

  // ---------- header ----------

  get periodLabel(): string {
    const active = this.calendar.activeDate;
    switch (this.calendar.currentView) {
      case 'month':
        return this.adapter.format(active, this.formats.display.monthYearLabel);   // October 2026
      case 'year':
        return String(this.adapter.getYear(active));
      default: {
        const start = this.pageStartYear();
        return `${start} \u2013 ${start + YEARS_PER_PAGE - 1}`;
      }
    }
  }

  get prevLabel(): string {
    return { month: 'Previous month', year: 'Previous year', 'multi-year': 'Previous 24 years' }[this.calendar.currentView];
  }

  get nextLabel(): string {
    return { month: 'Next month', year: 'Next year', 'multi-year': 'Next 24 years' }[this.calendar.currentView];
  }

  /** Clicking the month/year title switches between the month grid and the year chooser (same as Material). */
  toggleView(): void {
    this.calendar.currentView = this.calendar.currentView === 'month' ? 'multi-year' : 'month';
  }

  step(direction: -1 | 1): void {
    const view = this.calendar.currentView;
    const active = this.calendar.activeDate;
    this.calendar.activeDate =
      view === 'month'
        ? this.adapter.addCalendarMonths(active, direction)
        : this.adapter.addCalendarYears(active, view === 'year' ? direction : direction * YEARS_PER_PAGE);
  }

  /** Stops at the month / year that holds the min / max date. */
  canStep(direction: -1 | 1): boolean {
    const limit = direction < 0 ? this.calendar.minDate : this.calendar.maxDate;
    if (!limit) { return true; }
    const active = this.calendar.activeDate;
    const view = this.calendar.currentView;
    if (view === 'month') {
      return !(this.adapter.getYear(active) === this.adapter.getYear(limit)
        && this.adapter.getMonth(active) === this.adapter.getMonth(limit));
    }
    if (view === 'year') {
      return this.adapter.getYear(active) !== this.adapter.getYear(limit);
    }
    return true;
  }

  private pageStartYear(): number {
    const { activeDate, minDate, maxDate } = this.calendar;
    const activeYear = this.adapter.getYear(activeDate);
    let startingYear = 0;
    if (maxDate) {
      startingYear = this.adapter.getYear(maxDate) - YEARS_PER_PAGE + 1;
    } else if (minDate) {
      startingYear = this.adapter.getYear(minDate);
    }
    const offset = (((activeYear - startingYear) % YEARS_PER_PAGE) + YEARS_PER_PAGE) % YEARS_PER_PAGE;
    return activeYear - offset;
  }

  // ---------- footer ----------

  /** Today at local midnight (the same value a click on today's cell produces). */
  private todayValue(): D {
    const now = this.adapter.today();
    return this.adapter.createDate(this.adapter.getYear(now), this.adapter.getMonth(now), this.adapter.getDate(now));
  }

  /** Today respects the field's min / max / filter, exactly like the day cells do. */
  get todayAllowed(): boolean {
    const t = this.todayValue();
    const { minDate, maxDate, dateFilter } = this.calendar;
    if (minDate && this.adapter.compareDate(t, minDate) < 0) { return false; }
    if (maxDate && this.adapter.compareDate(t, maxDate) > 0) { return false; }
    if (dateFilter && !dateFilter(t)) { return false; }
    return true;
  }

  today(): void {
    const picker = this.content?.datepicker;
    if (!picker || !this.todayAllowed) { return; }
    picker.select(this.todayValue());   // updates the form control through the input, nothing is saved
    picker.close();
  }

  clear(): void {
    const picker = this.content?.datepicker;
    if (!picker) { return; }
    picker.select(null as unknown as D);   // control becomes null; Validators.required (if any) then shows its error
    picker.close();
  }
}

/**
 * Adds the modern header / footer to every <mat-datepicker>. Phones (<= 480px) get Material's centred
 * "touch" dialog so the calendar can never be clipped or pushed off-screen.
 */
@Directive({ selector: 'mat-datepicker', standalone: true })
export class VgModernDatepickerDirective implements OnInit {
  private readonly picker = inject(MatDatepicker, { self: true });

  ngOnInit(): void {
    if (!this.picker.calendarHeaderComponent) {
      this.picker.calendarHeaderComponent = VgCalendarHeaderComponent;
    }
    if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 480px)').matches) {
      this.picker.touchUi = true;
    }
  }
}